import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Outlet, unstable_useBlocker as useBlocker } from 'react-router-dom';

import { TranslationKeys } from 'app/translations';
import { ConfirmDialog } from 'app/components/ConfirmDialog';

type NavigationGuardContextValue = {
  setDirty: (sourceId: string, isDirty: boolean) => void;
};

const NavigationGuardContext = React.createContext<NavigationGuardContextValue | null>(null);

let sourceId = 0;

export const useNavigationGuard = () => {
  const context = React.useContext(NavigationGuardContext);

  if (!context) {
    throw new Error('useNavigationGuard must be used within NavigationGuard');
  }

  return context;
};

export const useNavigationGuardSourceId = () => {
  const sourceIdRef = React.useRef<string>();

  if (!sourceIdRef.current) {
    sourceId += 1;
    sourceIdRef.current = `navigation-guard-source-${sourceId}`;
  }

  return sourceIdRef.current;
};

type NavigationGuardProps = {
  children?: React.ReactNode;
};

export const NavigationGuard = ({ children }: NavigationGuardProps) => {
  const [dirtySources, setDirtySources] = React.useState<Record<string, boolean>>({});
  const isDirty = Object.values(dirtySources).some(Boolean);
  const blocker = useBlocker(isDirty);

  const setDirty = React.useCallback((sourceId: string, sourceIsDirty: boolean) => {
    setDirtySources(currentSources => {
      if (!sourceIsDirty) {
        if (!currentSources[sourceId]) {
          return currentSources;
        }

        const nextSources = { ...currentSources };
        delete nextSources[sourceId];
        return nextSources;
      }

      if (currentSources[sourceId]) {
        return currentSources;
      }

      return { ...currentSources, [sourceId]: true };
    });
  }, []);

  React.useEffect(() => {
    if (!isDirty) {
      return undefined;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      Reflect.set(event, 'returnValue', '');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const handleCancel = React.useCallback(() => {
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  }, [blocker]);
  const handleConfirm = React.useCallback(() => {
    if (blocker.state === 'blocked') {
      blocker.proceed();
    }
  }, [blocker]);
  const contextValue = React.useMemo(() => ({ setDirty }), [setDirty]);

  return (
    <NavigationGuardContext.Provider value={contextValue}>
      {children || <Outlet />}
      <ConfirmDialog
        open={blocker.state === 'blocked'}
        title={<FormattedMessage id={TranslationKeys.unsavedChanges_title} />}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        cancelButtonMessageId={TranslationKeys.unsavedChanges_stay}
        confirmButtonMessageId={TranslationKeys.unsavedChanges_leave}
      >
        <FormattedMessage id={TranslationKeys.unsavedChanges_message} />
      </ConfirmDialog>
    </NavigationGuardContext.Provider>
  );
};
